'use client';
import dynamic from 'next/dynamic';
import { useState, useSyncExternalStore } from 'react';
import { MOTION } from '../../constants';

const noopSubscribe = () => () => {};
/**
 * True on the server and while React hydrates this component, false when it mounts fresh on the client (client
 * navigations). Hydration runs in several passes with streaming, so a module flag set by the first mount is not enough.
 */
const useIsHydrating = () =>
  useSyncExternalStore(
    noopSubscribe,
    () => false,
    () => true,
  );

type transitionTypes =
  | 'fade'
  | 'fadeUp'
  | 'fadeDown'
  | 'fadeLeft'
  | 'fadeRight'
  | 'slideUp'
  | 'slideDown'
  | 'slideLeft'
  | 'slideRight'
  | 'fadeExpand'
  | 'disabled';

// Entrance animations in CSS: only initial -> animate ever ran (nothing used exit), so the motion library's ~38KB gz
// on every page bought nothing CSS can't do. The theme's reduced-motion rule makes them jump to the end
const EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';
const KEYFRAMES_FROM: Record<Exclude<transitionTypes, 'disabled'>, string> = {
  fade: 'opacity: 0',
  fadeUp: 'opacity: 0; transform: translateY(24px)',
  fadeDown: 'opacity: 0; transform: translateY(-24px)',
  fadeLeft: 'opacity: 0; transform: translateX(-24px)',
  fadeRight: 'opacity: 0; transform: translateX(24px)',
  slideUp: 'transform: translateY(24px)',
  slideDown: 'transform: translateY(-24px)',
  slideLeft: 'transform: translateX(-24px)',
  slideRight: 'transform: translateX(24px)',
  fadeExpand: 'opacity: 0; width: 0%; overflow: hidden',
};
const KEYFRAMES_TO: Partial<Record<transitionTypes, string>> = {
  fadeExpand: 'opacity: 1; width: 100%; overflow: hidden',
};
const KEYFRAMES = Object.entries(KEYFRAMES_FROM)
  .map(
    ([name, from]) =>
      `@keyframes directoTransition-${name}{from{${from}}to{${KEYFRAMES_TO[name as transitionTypes] ?? 'opacity: 1; transform: none'}}}`,
  )
  .join('');

/**
 * Renders a transition div that animates its children when rendered.
 *
 * Content in the first page load is server rendered and shown without the entrance animation: animating it in from
 * opacity 0 kept the main text invisible until the JS loaded (LCP). Transitions mounted later animate as before.
 *
 * @param {React.ReactNode} children - The content to be animated when rendered.
 * @param {transitionTypes} type - The animation type @default 'fade'
 * @param {Number} duration - Custom animation duration time override.
 * @param {Object} style - Styles override for the div component.
 * @return {React.JSX.Element} The rendered transition div with its children.
 */
const TransitionComponent = ({
  children,
  type = 'fade',
  mobileType,
  duration = MOTION.medium / 1000,
  delay = 0,
  style,
  id,
}: {
  children: React.ReactNode;
  type?: transitionTypes;
  mobileType?: transitionTypes;
  duration?: number;
  delay?: number;
  style?: {};
  id?: string;
}) => {
  // Frozen at mount: the hook turns false right after hydration, when the entrance no longer matters
  const isHydrating = useIsHydrating();
  const [skipEntrance] = useState(isHydrating);
  const isMobile = !skipEntrance && window.innerWidth < 900;
  /* c8 ignore start */
  const name = isMobile && mobileType ? mobileType : type;
  /* c8 ignore end */
  // 'backwards' drops the end keyframe once done: a kept transform, even an identity one, turns the div into the
  // containing block of fixed children (the menu drawer grew to the page height and scrolled with it). Only
  // fadeExpand ends on values other than the defaults, so it keeps them
  const fill = name in KEYFRAMES_TO ? 'both' : 'backwards';
  const animation =
    skipEntrance || name === 'disabled'
      ? undefined
      : `directoTransition-${name} ${duration}s ${EASE} ${delay}s ${fill}`;

  return (
    <>
      {style && id && (
        <style>
          {`
        #${id} {
          ${Object.entries(style)
            .map(([k, v]) => `${k}:${v}`)
            .join(';')}
        }
      `}
        </style>
      )}
      {/* href + precedence: React hoists it into <head> once, however many Transitions render */}
      <style href="directo-transitions" precedence="default">
        {KEYFRAMES}
      </style>
      <div id={id} style={animation ? { animation } : undefined}>
        {children}
      </div>
    </>
  );
};

export const Transition = TransitionComponent;

/**
 * Client-only Transition, for trees that render from browser-only state (media queries with noSsr, cookies) and
 * would not match the server markup.
 */
export const ClientOnlyTransition = dynamic(() => Promise.resolve(TransitionComponent), { ssr: false });
